import { useCallback } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { safeAssemblySwitch, isActivelyWorkingOnAssembly } from '../../utils/assemblyProtection';
import { SelectedPoint } from '../types/eventTypes';
import { calculateCenterOfPoints } from './mouseEventUtils';

export interface RightButtonEventProps {
  svgRef: React.RefObject<SVGSVGElement>;
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
  pastePositionRef: React.MutableRefObject<{ x: number; y: number } | null>;
  selectedPoints: SelectedPoint[];
  showPastePreview: boolean;
  pastePreviewPoints: React.MutableRefObject<any[]>;
  // setPastePreviewOffset: (offset: { x: number; y: number }) => void;
  setShowContextMenu: (show: boolean) => void;
  assembliesList: any[];
  copiedObjects: any[];
}

export const useRightButtonEvents = ({
  svgRef,
  cursorPositionRef,
  pastePositionRef,
  selectedPoints,
  showPastePreview,
  pastePreviewPoints,
  setShowContextMenu,
  assembliesList,
  copiedObjects,
}: RightButtonEventProps) => {
  const { setIsDragging, setIsEditingPoint, setHasPointEditorInteraction } =
    useStore(useUnityBuildStore);
  const { currentTakeoffAssembly, setCurrentTakeoffAssembly } = useStore(useAssemblyStore);
  const { setSelectedPoints, setHasCopiedItems } = useStore(useSelectionStore);

  /**
   * Handles right button (button2) mouse events
   */
  const handleTakeoffButton2MouseEvents = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!svgRef.current || e.button !== 2) return;

      // Check if the user is currently focused on a text input element
      const _target = e.target as HTMLElement;
      const activeElement = document.activeElement;

      // Prevent our handler if user is editing text or focused on text inputs
      if (
        activeElement &&
        (activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'INPUT' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          _target.tagName === 'TEXTAREA' ||
          _target.tagName === 'INPUT' ||
          _target.getAttribute('contenteditable') === 'true')
      ) {
        // Don't prevent default - let browser handle text selection context menu
        return;
      }

      e.preventDefault();

      // Check if we have an active paste preview
      const previewActive = showPastePreview && pastePreviewPoints.current.length > 0;

      // If preview is active, capture the current cursor position
      if (previewActive) {
        pastePositionRef.current = { ...cursorPositionRef.current };

        // Set a marker that the paste position is frozen (to show in point editor)
        (window as any).__pastePositionFrozen = true;

        // Store the frozen position globally so PointEditor can access it
        (window as any).__frozenPastePosition = { ...cursorPositionRef.current };

        // Calculate the paste preview offset so the paste action can use it
        if (pastePreviewPoints && pastePreviewPoints.current.length > 0) {
          const center = calculateCenterOfPoints(pastePreviewPoints.current);
          const offset = {
            x: cursorPositionRef.current.x - center.x,
            y: cursorPositionRef.current.y - center.y,
          };
          (window as any).__currentPastePreviewOffset = offset;
        }

        // Open point editor with paste options
        try {
          useAssemblyStore.getState().setIsViewingTakeoffEditor(true);
        } catch {}
        setHasPointEditorInteraction(false);
        // If a run was previously started via Add to Run, show Complete Run now
        try {
          if (useUnityBuildStore.getState().hasActiveRunStarted) {
            useUnityBuildStore.getState().setHasPointEditorInteraction(true);
          }
        } catch {}
        setIsEditingPoint({
          isEditingPoint: true,
          isViewingTakeoffEditor: true,
          x: e.clientX,
          y: e.clientY,
        });
        // Enable annotate mode so cursor preview shows immediately
        try {
          useUnityBuildStore.getState().setIsAnnotatingTakeoff(true);
        } catch {}

        setIsDragging(false);
        setShowContextMenu(false);
        return;
      }

      // Find which assembly contains the right-clicked point
      const target = e.target as SVGElement;

      // Check if we're actually clicking on an SVG element or a point
      // and not just panning the document
      const tagName = target.tagName?.toLowerCase();
      const isSvgElement =
        tagName &&
        ['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g'].includes(tagName);

      // Take a snapshot of the current cursor position when point editor opens
      // This ensures paste operations use this position, not wherever cursor moves to click the button
      if (cursorPositionRef.current) {
        // Only set pastePositionRef if we have an active paste preview
        // Otherwise leave it null so future previews will follow the cursor
        if (showPastePreview && pastePreviewPoints.current.length > 0) {
          pastePositionRef.current = { ...cursorPositionRef.current };
        }
      }

      const pointId = target.getAttribute('data-pointid');

      // If we have a point directly under the cursor, handle it first
      if (pointId) {
        // Use memoized assembliesList to find the assembly containing this point more efficiently
        const assembly = assembliesList.find(a =>
          a.pointsList?.some((p: { pointid: string }) => p.pointid === pointId)
        );

        if (assembly) {
          // Check if we're actively working on a different assembly
          if (
            assembly.assemblyid !== currentTakeoffAssembly?.assemblyid &&
            isActivelyWorkingOnAssembly()
          ) {
            // Silently block interaction with points from different assemblies
            return;
          }

          // Use safe assembly switch to protect active work
          const switchAllowed = safeAssemblySwitch(
            assembly.assemblyid,
            currentTakeoffAssembly?.assemblyid || null,
            () => setCurrentTakeoffAssembly(assembly)
          );

          // Only proceed with point editing if switch was allowed
          if (!switchAllowed) {
            return;
          }

          // Find the point index for editing
          const pointIndex =
            assembly.pointsList?.findIndex((p: { pointid: string }) => p.pointid === pointId) ?? -1;

          if (pointIndex >= 0) {
            const assemblyIndex =
              assembliesList.findIndex(a => a.assemblyid === assembly.assemblyid) ?? 0;

            try {
              useAssemblyStore.getState().setIsViewingTakeoffEditor(true);
            } catch {}
            setHasPointEditorInteraction(false);
            try {
              if (useUnityBuildStore.getState().hasActiveRunStarted) {
                useUnityBuildStore.getState().setHasPointEditorInteraction(true);
              }
            } catch {}
            setIsEditingPoint({
              isEditingPoint: true,
              isViewingTakeoffEditor: true,
              x: e.clientX,
              y: e.clientY,
              assemblyIndex: assemblyIndex,
              pointIndex: pointIndex,
            });
            // Enable annotate mode so cursor preview shows immediately
            try {
              useUnityBuildStore.getState().setIsAnnotatingTakeoff(true);
            } catch {}
          }
        }
      }
      // IMPROVEMENT: If there are selected points, always open the point editor, regardless of where clicked
      else if (selectedPoints.length > 0) {
        // If we have selected points, open the point editor for those selected points
        // regardless of whether we're clicking directly on an SVG element
        try {
          useAssemblyStore.getState().setIsViewingTakeoffEditor(true);
        } catch {}
        setHasPointEditorInteraction(false);
        try {
          if (useUnityBuildStore.getState().hasActiveRunStarted) {
            useUnityBuildStore.getState().setHasPointEditorInteraction(true);
          }
        } catch {}
        setIsEditingPoint({
          isEditingPoint: true,
          isViewingTakeoffEditor: true,
          x: e.clientX,
          y: e.clientY,
        });
        // Enable annotate mode so cursor preview shows immediately
        try {
          useUnityBuildStore.getState().setIsAnnotatingTakeoff(true);
        } catch {}
      }
      // If we have an active assembly, open the point editor only if clicking within the SVG area
      else if (currentTakeoffAssembly && isSvgElement) {
        // If we have a current takeoff assembly and we're clicking in the SVG area,
        // open the point editor
        try {
          useAssemblyStore.getState().setIsViewingTakeoffEditor(true);
        } catch {}
        setHasPointEditorInteraction(false);
        try {
          if (useUnityBuildStore.getState().hasActiveRunStarted) {
            useUnityBuildStore.getState().setHasPointEditorInteraction(true);
          }
        } catch {}
        setIsEditingPoint({
          isEditingPoint: true,
          isViewingTakeoffEditor: true,
          x: e.clientX,
          y: e.clientY,
        });
        // Enable annotate mode so cursor preview shows immediately
        try {
          useUnityBuildStore.getState().setIsAnnotatingTakeoff(true);
        } catch {}
      }

      // Update the store with current selection
      if (selectedPoints.length > 0) {
        setSelectedPoints(selectedPoints);
        setHasCopiedItems(copiedObjects.length > 0);
      }

      setIsDragging(false);
      setShowContextMenu(false);
    },
    [
      svgRef,
      cursorPositionRef,
      pastePositionRef,
      selectedPoints,
      showPastePreview,
      pastePreviewPoints,
      setIsDragging,
      setIsEditingPoint,
      copiedObjects,
      assembliesList,
      setCurrentTakeoffAssembly,
      setSelectedPoints,
      setHasCopiedItems,
      setShowContextMenu,
      currentTakeoffAssembly,
      setHasPointEditorInteraction,
    ]
  );

  return { handleTakeoffButton2MouseEvents };
};
